/**
 * PublicPricing — Public marketing pricing page.
 *
 * Renders a single-glance comparison matrix (rows = feature bullets,
 * columns = plans) auto-generated from the plans returned by the
 * public /api/plans endpoint. Zero double-maintenance: whenever an
 * admin edits a plan's bullets in /admin/subscription-plans the
 * matrix updates on next page load.
 *
 * Plans flagged `is_public: false` by the admin are hidden here
 * automatically (the /plans endpoint filters them out server-side).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Sparkles, Loader2 } from "lucide-react";

function money(n) {
    return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
}

export default function PublicPricing() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/plans");
                setPlans(data?.plans || []);
            } catch { /* leave empty state */ }
            finally { setLoading(false); }
        })();
    }, []);

    // Derive the union of bullets across plans — one row per unique bullet
    // in declaration order. For each cell we mark ✓ if the plan lists that
    // exact bullet. This turns free-form marketing copy into a clean
    // side-by-side matrix without asking admins to maintain two data shapes.
    const matrix = useMemo(() => {
        const seen = new Set();
        const rows = [];
        for (const p of plans) {
            for (const f of (p.features || [])) {
                if (!seen.has(f)) { seen.add(f); rows.push(f); }
            }
        }
        return rows;
    }, [plans]);

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ikb" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="public-pricing">
            <div className="max-w-7xl mx-auto px-4 py-10">
                <header className="text-center mb-10">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Pricing</div>
                    <h1 className="font-display font-black text-3xl sm:text-5xl leading-tight mt-2">Simple, transparent pricing</h1>
                    <p className="text-sm text-muted-foreground mt-3 max-w-2xl mx-auto">
                        Pick the plan that fits your team. Cancel or change any time from Billing settings.
                    </p>
                </header>

                {plans.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground py-12">Pricing coming soon.</div>
                ) : (
                    <>
                        {/* Plan cards (top) */}
                        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(plans.length, 4)} gap-4 mb-10`}>
                            {plans.map((p) => (
                                <div key={p.key} className={`bg-white border p-6 relative ${p.popular ? "border-ikb border-2" : "border-border"}`} data-testid={`public-plan-${p.key}`}>
                                    {p.popular && (
                                        <div className="absolute -top-3 left-4 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5">Most popular</div>
                                    )}
                                    <div className="font-display font-black text-xl">{p.name}</div>
                                    <div className="mt-2">
                                        <span className="font-display font-black text-3xl tabular-nums">{money(p.price_inr)}</span>
                                        <span className="text-xs text-muted-foreground ml-1">/mo</span>
                                    </div>
                                    <ul className="mt-4 space-y-1.5 text-xs">
                                        {(p.features || []).slice(0, 6).map((f, i) => (
                                            <li key={i} className="flex items-start gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to={`/signup?plan=${p.key}`}
                                        className={`mt-5 block text-center h-10 leading-10 text-xs font-mono uppercase tracking-widest ${p.popular ? "bg-ikb text-white hover:bg-ikb/90" : "border border-ikb text-ikb hover:bg-ikb/5"}`}
                                        data-testid={`public-cta-${p.key}`}
                                    >
                                        Start with {p.name}
                                    </Link>
                                </div>
                            ))}
                        </div>

                        {/* Comparison matrix */}
                        <div className="border border-border bg-white overflow-x-auto" data-testid="public-comparison-matrix">
                            <div className="px-4 py-3 border-b border-border bg-neutral-50 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-ikb" /> Compare plans side-by-side
                            </div>
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50/50 border-b border-border">
                                    <tr>
                                        <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest w-1/3">Feature</th>
                                        {plans.map((p) => (
                                            <th key={p.key} className="text-center px-3 py-3 text-[10px] font-mono uppercase tracking-widest">
                                                <div>{p.name}</div>
                                                <div className="font-display font-black text-base tabular-nums normal-case tracking-normal text-foreground mt-1">{money(p.price_inr)}<span className="text-[9px] text-muted-foreground">/mo</span></div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Numeric quotas — always shown */}
                                    <QuotaRow label="Recruiter seats" plans={plans} field="seats" />
                                    <QuotaRow label="Active jobs" plans={plans} field="job_quota" />
                                    <QuotaRow label="Candidates" plans={plans} field="candidates_quota" />
                                    {/* Feature bullet rows */}
                                    {matrix.map((row) => (
                                        <tr key={row} className="border-b border-border last:border-b-0">
                                            <td className="px-4 py-2 text-xs">{row}</td>
                                            {plans.map((p) => (
                                                <td key={p.key} className="px-3 py-2 text-center">
                                                    {(p.features || []).includes(row)
                                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                                                        : <span className="text-muted-foreground">—</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-center mt-10">
                            <Link to="/signup" className="inline-block h-11 px-6 leading-[44px] bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-ikb/90" data-testid="public-pricing-signup">
                                Create your workspace →
                            </Link>
                            <div className="text-[11px] text-muted-foreground mt-2">14-day risk-free · cancel any time</div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function QuotaRow({ label, plans, field }) {
    return (
        <tr className="border-b border-border bg-neutral-50/40">
            <td className="px-4 py-2 text-xs font-medium">{label}</td>
            {plans.map((p) => {
                const v = p[field];
                const isUnlimited = v >= 999 || v === null || v === undefined;
                return (
                    <td key={p.key} className="px-3 py-2 text-center text-xs font-mono tabular-nums">
                        {isUnlimited ? "Unlimited" : Number(v).toLocaleString("en-IN")}
                    </td>
                );
            })}
        </tr>
    );
}
